import { getCookieValue } from 'global-utils/cookie-utils';

declare global {
  interface Window {
    Shopify?: { [key: string]: any };
  }
}

type ShopifyConsentState = {
  marketing: boolean;
  analytics: boolean;
  preferences: boolean;
};

// Storefront API 2025-10+ @inContext(visitorConsent) shape.
// Host apps pass this object into their GraphQL queries so Shopify encodes
// consent into the returned checkoutUrl via the _cs parameter.
type ShopifyVisitorConsent = {
  analytics: boolean;
  preferences: boolean;
  marketing: boolean;
  saleOfData: boolean;
};

// Shopify Customer Privacy API integration
//
// Theme storefronts expose window.Shopify.customerPrivacy. Headless storefronts
// use getShopifyVisitorConsent with Storefront API GraphQL context instead.
//
// The consumer MUST ensure the Shopify consent-tracking-api script loads
// before cbgCompPack.js. initShopifyConsent() checks synchronously and
// logs a warning if the API is not yet available.

// Keeps the repeated Shopify availability check in one place so both startup
// sync and banner interaction fail consistently when the API is absent.
const hasShopifyCustomerPrivacyAPI = () =>
  !!(
    window.Shopify &&
    window.Shopify.customerPrivacy &&
    window.Shopify.customerPrivacy.setTrackingConsent
  );

// Gets the current consent state from Shopify's Customer Privacy API.
// Returns undefined if the API is not available or consent hasn't been set.
const getCurrentShopifyConsent = (): ShopifyConsentState | undefined => {
  if (!hasShopifyCustomerPrivacyAPI()) {
    return undefined;
  }

  try {
    const currentConsent = window.Shopify?.customerPrivacy.getTrackingConsent();

    if (!currentConsent) {
      return undefined;
    }

    return {
      marketing: currentConsent.marketing === true,
      analytics: currentConsent.analytics === true,
      preferences: currentConsent.preferences === true
    };
  } catch (error) {
    console.error('cbgCConsent: Error reading Shopify consent state:', error);
    return undefined;
  }
};

// Compares two consent states to determine if they match.
// Returns true if all consent values are identical.
const shopifyConsentMatches = (
  state1: ShopifyConsentState,
  state2: ShopifyConsentState
): boolean => {
  return (
    state1.marketing === state2.marketing &&
    state1.analytics === state2.analytics &&
    state1.preferences === state2.preferences
  );
};

// Central submit function used by both page-load sync and banner interaction.
const submitShopifyConsent = (
  cbgConsentMappedToShopify: ShopifyConsentState
) => {
  window.Shopify?.customerPrivacy.setTrackingConsent(cbgConsentMappedToShopify);
};

// Shared lower-level helpers

// Reads the persisted CBG consent cookie and maps CBG category names to
// Shopify's Customer Privacy API fields.
const getShopifyConsentFromCbgCookie = (): ShopifyConsentState | undefined => {
  const consentValues = getCookieValue('cbg_ccp');

  if (!consentValues) {
    return undefined;
  }

  return {
    marketing: consentValues.includes('marketing'),
    analytics: consentValues.includes('performance'),
    preferences: consentValues.includes('functional')
  };
};

// Converts the consent array emitted by the CBG banner into the same normalized
// consent state used by the page-load cookie sync.
const getShopifyConsentFromConsentList = (
  consent: string[]
): ShopifyConsentState => {
  const consentValues = consent[0]
    .split(',')
    .reduce<{ [key: string]: boolean }>((acc, consentValue) => {
      acc[consentValue] = true;
      return acc;
    }, {});

  return {
    marketing: !!consentValues.marketing,
    analytics: !!consentValues.performance,
    preferences: !!consentValues.functional
  };
};

// Shared CBG-to-Shopify consent sync

// Page-load sync path. It reads a previously saved cbg_ccp cookie and mirrors
// that choice into Shopify after the Customer Privacy API is available.
// Only submits if the consent states differ to avoid unnecessary updates.

// syncShopifyConsentWithCbgCookie() for eligible page-load migration
const syncShopifyConsentWithCbgCookie = () => {
  if (!hasShopifyCustomerPrivacyAPI()) {
    console.error(
      'cbgCConsent: Shopify object is not available on the window. unable to sync consent.'
    );
    return;
  }

  const cbgConsentMappedToShopify = getShopifyConsentFromCbgCookie();

  if (!cbgConsentMappedToShopify) {
    return;
  }

  // Check if current Shopify consent already matches the CBG cookie
  const currentShopifyConsent = getCurrentShopifyConsent();
  if (
    currentShopifyConsent &&
    shopifyConsentMatches(currentShopifyConsent, cbgConsentMappedToShopify)
  ) {
    // Consent already matches, no need to update
    return;
  }

  submitShopifyConsent(cbgConsentMappedToShopify);
};

// Banner-interaction path. Whenever the CBG banner changes, this converts the
// selected CBG categories into Shopify's marketing/analytics/preferences fields
// and submits them to Shopify.
// onShopifyConsentChange() when the visitor changes the CBG banner choice.
const onShopifyConsentChange = (consent: string[]) => {
  if (!hasShopifyCustomerPrivacyAPI()) {
    console.error(
      'cbgCConsent: Shopify object is not available on the window. Make sure you are running this on a Shopify store.'
    );
    return;
  }

  if (!consent || consent.length === 0 || typeof consent[0] !== 'string') {
    return;
  }

  const cbgConsentMappedToShopify = getShopifyConsentFromConsentList(consent);
  submitShopifyConsent(cbgConsentMappedToShopify);
};

const initShopifyConsent = () => {
  if (hasShopifyCustomerPrivacyAPI()) {
    syncShopifyConsentWithCbgCookie();
    return;
  }

  console.warn(
    'cbgCConsent: Shopify Customer Privacy API was not detected. ' +
      'Ensure the Shopify consent-tracking-api script loads before cbgCompPack.js.'
  );
};

// Returns the current CBG consent mapped to Shopify's @inContext(visitorConsent)
// GraphQL directive shape (Storefront API 2025-10+). Host apps use this value
// in their Storefront API queries so checkoutUrl encodes consent via _cs.
const getShopifyVisitorConsent = (): ShopifyVisitorConsent => {
  const cbgConsent = getShopifyConsentFromCbgCookie();

  return {
    analytics: cbgConsent?.analytics ?? false,
    preferences: cbgConsent?.preferences ?? false,
    marketing: cbgConsent?.marketing ?? false,
    saleOfData: false
  };
};

export {
  initShopifyConsent,
  onShopifyConsentChange,
  syncShopifyConsentWithCbgCookie,
  getShopifyVisitorConsent
};
